import { useEffect, useRef } from "react";
import { motion } from "framer-motion";

export default function SuccessScreen({ orderData, onNewOrder, onTrack }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;
    const ctx = canvas.getContext("2d");
    const COLORS = ["#D94F00","#ff7a2f","#ffd700","#1A7F4B","#3B82F6","#EC4899","#fff"];
    const particles = [];

    const burst = (x, y) => {
      for (let i = 0; i < 60; i++) {
        const angle = (Math.PI * 2 / 60) * i;
        const speed = 2 + Math.random() * 5;
        particles.push({
          x, y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed - 2,
          alpha: 1,
          color: COLORS[Math.floor(Math.random() * COLORS.length)],
          r: 2 + Math.random() * 3,
          gravity: 0.08,
          decay: 0.012 + Math.random() * 0.01,
        });
      }
    };

    // Fire 4 bursts at staggered times
    const W = canvas.width, H = canvas.height;
    const shots = [
      { x: W * 0.25, y: H * 0.3, delay: 100 },
      { x: W * 0.75, y: H * 0.25, delay: 400 },
      { x: W * 0.5,  y: H * 0.2,  delay: 700 },
      { x: W * 0.15, y: H * 0.4,  delay: 1100 },
      { x: W * 0.85, y: H * 0.35, delay: 1400 },
    ];
    const timers = shots.map(s => setTimeout(() => burst(s.x, s.y), s.delay));

    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.vy += p.gravity;
        p.x += p.vx; p.y += p.vy;
        p.alpha -= p.decay;
        if (p.alpha <= 0) { particles.splice(i, 1); continue; }
        ctx.save();
        ctx.globalAlpha = p.alpha;
        ctx.fillStyle = p.color;
        ctx.shadowBlur = 4; ctx.shadowColor = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      }
      raf = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      timers.forEach(clearTimeout);
      cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="success-wrap">
      <div className="success-glow" />
      <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0 }} />

      <motion.svg initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 20, delay: 0.1 }} className="success-check-svg" width="88" height="88" viewBox="0 0 88 88" fill="none"
        style={{ marginBottom: "1.5rem", position: "relative", zIndex: 1 }}>
        <circle cx="44" cy="44" r="40" stroke="#1A7F4B" strokeWidth="4" fill="rgba(26,127,75,0.12)" />
        <path d="M26 44l13 13 23-26" stroke="#1A7F4B" strokeWidth="4.5" strokeLinecap="round" strokeLinejoin="round" fill="none"/>
      </motion.svg>

      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="success-title">Order Placed</motion.div>
      <motion.div initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="success-sub">
        Hi {orderData.name}, your {orderData.tokenType} order<br />
        is confirmed and being prepared.
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }} className="success-card">
        <div className="order-id-lbl">Order Reference</div>
        <div className="order-id-val">{orderData.order_id || orderData.razorpay_order_id}</div>
        <div style={{ marginTop:10, paddingTop:10, borderTop:"1px solid var(--border)" }}>
          <div style={{ fontSize:13, color:"var(--mid)", marginBottom:4 }}>
            {orderData.items.map(i => `${i.name} ×${i.qty}`).join(", ")}
          </div>
          <div style={{ fontFamily:"'Sora', sans-serif", fontSize:16, fontWeight:700, color:"var(--primary)" }}>
            Total: ₹{orderData.total}
          </div>
        </div>
      </motion.div>

      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="wa-row">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1A7F4B" strokeWidth="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.18 1.18 2 2 0 012.18 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.91 7.91a16 16 0 006.26 6.26l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
        <div className="wa-txt">Confirmation sent to +91 {orderData.phone}</div>
      </motion.div>

      <motion.button initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }} className="btn-primary" onClick={onTrack} style={{ position: "relative", zIndex: 1, marginBottom: 10 }}>
        Track My Order
      </motion.button>

      <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.7 }} onClick={onNewOrder} style={{
        width: "100%", background: "transparent", color: "rgba(255,255,255,0.5)",
        border: "1px solid rgba(255,255,255,0.15)", borderRadius: "var(--radius)",
        padding: "13px", fontSize: 14, fontWeight: 600, cursor: "pointer",
        transition: "border-color 0.2s, color 0.2s", position: "relative", zIndex: 1,
      }}
        onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.35)"; e.currentTarget.style.color = "rgba(255,255,255,0.8)"; }}
        onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.15)"; e.currentTarget.style.color = "rgba(255,255,255,0.5)"; }}
      >
        Place Another Order
      </motion.button>
    </motion.div>
  );
}

